import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import LangSwitch from "@/components/LangSwitch";
import { NAV_LINKS } from "@/data/site";
import { scrollToHash, scrollAfterNavigation } from "@/lib/scroll";
import { useLang } from "@/i18n/LanguageContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const closeRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
      const to = window.setTimeout(() => closeRef.current?.focus(), 120);
      const onKey = (e) => e.key === "Escape" && setOpen(false);
      window.addEventListener("keydown", onKey);
      return () => {
        window.clearTimeout(to);
        window.removeEventListener("keydown", onKey);
      };
    }
    window.__lenis?.start();
    document.body.style.overflow = "";
  }, [open]);

  const go = (hash) => (e) => {
    e.preventDefault();
    window.__lenis?.start();
    document.body.style.overflow = "";
    setOpen(false);
    if (hash === "#top") {
      if (pathname !== "/") navigate("/");
      else scrollToHash("body");
      return;
    }
    if (pathname !== "/") {
      navigate("/");
      scrollAfterNavigation(hash);
    } else {
      scrollToHash(hash);
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled
            ? "border-b border-gold/15 bg-midnight/90 backdrop-blur-md"
            : "bg-midnight/35 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-[1680px] items-center justify-between px-6 lg:px-12">
          <Link to="/" aria-label={t.aria.homeLogo} data-testid="nav-home-logo" className="shrink-0">
            <Logo variant="nav" />
          </Link>

          <nav
            className="hidden items-center gap-9 whitespace-nowrap 2xl:flex"
            data-testid="desktop-nav"
          >
            {NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={l.hash}
                onClick={go(l.hash)}
                data-testid={`nav-link-${l.id}`}
                className="font-sans text-[11px] tracking-[0.28em] text-ivory/70 transition-colors duration-300 hover:text-gold"
              >
                {t.nav[l.id]}
              </a>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-4">
            <LangSwitch />
            <button
              onClick={() => setOpen(true)}
              aria-label={t.aria.openMenu}
              aria-expanded={open}
              data-testid="mobile-menu-btn"
              className="-m-2 p-2 text-ivory transition-colors duration-300 hover:text-gold 2xl:hidden"
            >
              <Menu size={26} strokeWidth={1.25} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-[70] flex flex-col bg-midnight"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex h-[72px] items-center justify-between px-6">
              <Logo variant="nav" />
              <button
                ref={closeRef}
                onClick={() => setOpen(false)}
                aria-label={t.aria.closeMenu}
                data-testid="mobile-menu-close-btn"
                className="-m-2 p-2 text-ivory transition-colors duration-300 hover:text-gold"
              >
                <X size={28} strokeWidth={1.25} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={l.hash}
                  onClick={go(l.hash)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="border-b border-gold/10 py-4 font-serif text-4xl font-light text-ivory transition-colors duration-300 hover:text-gold"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  {t.nav[l.id]}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.6 }}
                className="pt-10"
              >
                <Link
                  to="/quote"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-get-quote-btn"
                  className="block bg-gold py-4 text-center font-sans text-xs tracking-[0.3em] text-midnight transition-colors duration-300 hover:bg-gold-light"
                >
                  {t.quoteCta}
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65, duration: 0.6 }}
                className="pt-8"
              >
                <LangSwitch className="justify-center text-sm" />
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
